import type { useTranslation } from 'react-i18next';
import type { SmsStatus } from '@minty/shared/sms';

/**
 * What a message's status is called, in the desk's words.
 *
 * Shared by the contact's feed and the navbar's recent-texts panel. The strings
 * live in the `tasks` namespace, named at every call: `t` arrives as a
 * parameter and the extractor reads the call, not what the caller bound, so
 * without `ns` it files them under `translation` and prunes the Polish.
 *
 * Null when the message carries no status at all: there is nothing to say, and
 * a caller showing "undefined" would be saying something.
 */
export function getSmsStatusLabel(
  status: SmsStatus | null | undefined,
  t: ReturnType<typeof useTranslation<'tasks'>>['t'],
) {
  switch (status) {
    case undefined:
    case null:
      return null;
    case 'received':
      return t('SMS received', { ns: 'tasks' });
    case 'pending':
      return t('SMS queued', { ns: 'tasks' });
    case 'queued':
      return t('SMS queued', { ns: 'tasks' });
    case 'sending':
      return t('SMS sending', { ns: 'tasks' });
    case 'sent':
      return t('SMS sent', { ns: 'tasks' });
    case 'delivered':
      return t('SMS delivered', { ns: 'tasks' });
    case 'delivery_unconfirmed':
      return t('SMS delivery unconfirmed', { ns: 'tasks' });
    case 'sending_failed':
      return t('SMS sending failed', { ns: 'tasks' });
    case 'delivery_failed':
      return t('SMS delivery failed', { ns: 'tasks' });
    case 'submission_failed':
      return t('SMS submission failed', { ns: 'tasks' });
  }
}
